import { createContext, JSX, ReactNode, useContext } from 'react';
import * as vp from 'vopay';
import { useClientData } from './ClientData.provider';

export type LockedFeaturesCtx = {
  GCMEnabled?: boolean;
  EFTCollectEnabled?: boolean;
  EFTSendEnabled?: boolean;
  ACHFundEnabled?: boolean;
  ACHWithdrawEnabled?: boolean;
  USDEFTCollectEnabled?: boolean;
  USDEFTSendEnabled?: boolean;
  RTRWithdrawEnabled?: boolean;
  RTPWithdrawEnabled?: boolean;
  FedwireWithdrawEnabled?: boolean;
  FedwireFundEnabled?: boolean;
  AFTEnabled?: boolean;
  InteracMoneyRequestEnabled?: boolean;
  InboundEtransferEnabled?: boolean;
  InteracBulkPayoutEnabled?: boolean;
  PortalViewOnly?: boolean;
  MoneyRequestGenerateUrlEnabled?: boolean;
  KYCLightEnabled?: boolean;
  SubAccountPermissions?: boolean;
  ClientAccountsEnabled?: boolean;
  AutoBalanceTransferEnabled?: boolean;
  TransactionLabelOverrideEnabled?: boolean;
  IQ11StandardEnabled?: boolean;
  IQ11DataEnrichingEnabled?: boolean;
  IQ11DataEnrichingEnhancedEnabled?: boolean;
  IQ11BoostEnabled?: boolean;
  IQ11PlusEnabled?: boolean;
  IQ11AdvantageEnabled?: boolean;
  TransactionServiceDefault?: boolean;
  PayLinkEnabled?: boolean;
  CustomFlinksCredentialsSet?: boolean;
  CustomPlaidCredentialsSet?: boolean;
  CustomInveriteCredentialsSet?: boolean;
  EnablePaylinkDecline?: boolean;
  CreditCardEnabled?: boolean;
  GooglePayEnabled?: boolean;
  ApplePayEnabled?: boolean;
  UserManagementEnabled?: boolean;
  AccountingSoftwareIntegrationEnabled?: boolean;
  AccountingSystemIntegrationEnabled?: boolean;
  AccountTransferEnabled?: boolean;
  PushToCardEnabled?: boolean;
  RealTimeMoneyRequestEnabled?: boolean;
  CustomGlobalPaySharedSecret?: boolean;
  LockedSelectOptions?: { [key: string]: string };
  BrandingEmailEnabled?: boolean;
  BrandingELinxEnabled?: boolean;
  BrandingPortalEnabled?: boolean;
  BrandingOnboardingEnabled?: boolean;
  TransactionApprovalWorkflowEnabled?: boolean;
  EmailDomainEnabled?: boolean;
  ChatSupportEnabled?: boolean;
  InterestAccountEnabled?: boolean;
  MicroDepositBankAccountVerificationEnabled?: boolean;
  InteracVerificationEnabled?: boolean;
  BillPayEnabled?: boolean;
  VirtualAccountsEnabled?: boolean;
  AccountSummaryEnabled?: boolean;
  AccountAndBillingEnabled?: boolean;
  FundMyAccountEnabled?: boolean;
  WithdrawMyAccountEnabled?: boolean;
  BankAccountTransferEnabled?: boolean;
  OperationalAccountsEnabled?: boolean;
  PartnerBillsAccount?: boolean;
  PaymentsEnabled?: boolean;
  CollectionsEnabled?: boolean;
  ShowSubAccountOrgChart?: boolean;
  InteracBasicVerificationEnabled?: boolean;
  InteracEnhancedVerificationEnabled?: boolean;
  CardBasicVerificationEnabled?: boolean;
  CardEnhancedVerificationEnabled?: boolean;
  SupportTicketEnabled?: boolean;
  DigitalWalletsEnabled?: boolean;
  PaypalWithdrawEnabled?: boolean;
  VenmoWithdrawEnabled?: boolean;
  ZelleWithdrawEnabled?: boolean;
  FileConversionEnabled?: boolean;
  FileSubmissionEnabled?: boolean;
};

export type LockedType = {
  id: string;
  name: string;
  locked: boolean;
};

export const LockedFeaturesContext = createContext<LockedFeaturesCtx>({});

export const LockedFeaturesProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { response } = useClientData();

  const values: LockedFeaturesCtx = {
    GCMEnabled: vp.toBool(response.GCMEnabled),
    EFTCollectEnabled: vp.toBool(response.EFTCollectEnabled),
    EFTSendEnabled: vp.toBool(response.EFTSendEnabled),
    ACHFundEnabled: vp.toBool(response.ACHFundEnabled),
    ACHWithdrawEnabled: vp.toBool(response.ACHWithdrawEnabled),
    USDEFTCollectEnabled: vp.toBool(response.USDEFTCollectEnabled),
    USDEFTSendEnabled: vp.toBool(response.USDEFTSendEnabled),
    RTRWithdrawEnabled: vp.toBool(response.RTRWithdrawEnabled),
    RTPWithdrawEnabled: vp.toBool(response.RTPWithdrawEnabled),
    FedwireWithdrawEnabled: vp.toBool(response.FedwireWithdrawEnabled),
    FedwireFundEnabled: vp.toBool(response.FedwireFundEnabled),
    AFTEnabled: response.AFTEnabled,
    InteracMoneyRequestEnabled: vp.toBool(response.InteracMoneyRequestEnabled),
    InboundEtransferEnabled: vp.toBool(response.InboundEtransferEnabled),
    InteracBulkPayoutEnabled: vp.toBool(response.InteracBulkPayoutEnabled),
    PortalViewOnly: vp.toBool(response.PortalViewOnly),
    MoneyRequestGenerateUrlEnabled: vp.toBool(response.MoneyRequestGenerateUrlEnabled),
    KYCLightEnabled: vp.toBool(response.KYCLightEnabled),
    SubAccountPermissions: vp.toBool(response.SubAccountPermissions),
    ClientAccountsEnabled: vp.toBool(response.ClientAccountsEnabled),
    AutoBalanceTransferEnabled: vp.toBool(response.AutoBalanceTransferEnabled),
    TransactionLabelOverrideEnabled: vp.toBool(response.TransactionLabelOverrideEnabled),
    IQ11StandardEnabled: vp.toBool(response.IQ11StandardEnabled),
    IQ11DataEnrichingEnabled: response.IQ11DataEnrichingEnabled,
    IQ11DataEnrichingEnhancedEnabled: response.IQ11DataEnrichingEnhancedEnabled,
    IQ11BoostEnabled: vp.toBool(response.IQ11BoostEnabled),
    IQ11PlusEnabled: vp.toBool(response.IQ11PlusEnabled),
    IQ11AdvantageEnabled: vp.toBool(response.IQ11AdvantageEnabled),
    TransactionServiceDefault: vp.toBool(response.TransactionServiceDefault),
    PayLinkEnabled: vp.toBool(response.PayLinkEnabled),
    CustomFlinksCredentialsSet: response.CustomFlinksCredentialsSet,
    CustomPlaidCredentialsSet: response.CustomPlaidCredentialsSet,
    CustomInveriteCredentialsSet: response.CustomInveriteCredentialsSet,
    EnablePaylinkDecline: vp.toBool(response.EnablePaylinkDecline),
    CreditCardEnabled: vp.toBool(response.CreditCardEnabled),
    GooglePayEnabled: response.GooglePayEnabled,
    ApplePayEnabled: response.ApplePayEnabled,
    UserManagementEnabled: vp.toBool(response.UserManagementEnabled),
    AccountingSoftwareIntegrationEnabled: response.AccountingSoftwareIntegrationEnabled,
    AccountingSystemIntegrationEnabled: vp.toBool(response.AccountingSystemIntegrationEnabled),
    AccountTransferEnabled: vp.toBool(response.AccountTransferEnabled),
    PushToCardEnabled: vp.toBool(response.PushToCardEnabled),
    RealTimeMoneyRequestEnabled: vp.toBool(response.RealTimeMoneyRequestEnabled),
    CustomGlobalPaySharedSecret: response.CustomGlobalPaySharedSecret,
    LockedSelectOptions: createLockedSelectOptions(),
    BrandingEmailEnabled: vp.toBool(response.BrandingEmailEnabled),
    BrandingELinxEnabled: vp.toBool(response.BrandingELinxEnabled),
    BrandingPortalEnabled: vp.toBool(response.BrandingPortalEnabled),
    BrandingOnboardingEnabled: vp.toBool(response.BrandingOnboardingEnabled),
    TransactionApprovalWorkflowEnabled: vp.toBool(response.TransactionApprovalWorkflowEnabled),
    EmailDomainEnabled: vp.toBool(response.EmailDomainEnabled),
    InterestAccountEnabled: response.InterestAccountEnabled,
    ChatSupportEnabled: response.ChatSupportEnabled,
    InteracVerificationEnabled: response.InteracVerificationEnabled,
    InteracBasicVerificationEnabled: response.InteracBasicVerificationEnabled,
    InteracEnhancedVerificationEnabled: response.InteracEnhancedVerificationEnabled,
    BillPayEnabled: response.BillPayEnabled,
    VirtualAccountsEnabled: response.VirtualAccountsEnabled,
    AccountSummaryEnabled: response.AccountSummaryEnabled,
    AccountAndBillingEnabled: response.AccountAndBillingEnabled,
    FundMyAccountEnabled: response.FundMyAccountEnabled,
    WithdrawMyAccountEnabled: response.WithdrawMyAccountEnabled,
    BankAccountTransferEnabled: response.BankAccountTransferEnabled,
    OperationalAccountsEnabled: response.OperationalAccountsEnabled,
    PartnerBillsAccount: response.PartnerBillsAccount,
    PaymentsEnabled: response.PaymentsEnabled,
    CollectionsEnabled: response.CollectionsEnabled,
    ShowSubAccountOrgChart: response.ShowSubAccountOrgChart,
    MicroDepositBankAccountVerificationEnabled: response.MicroDepositBankAccountVerificationEnabled,
    CardBasicVerificationEnabled: response.CardBasicVerificationEnabled,
    CardEnhancedVerificationEnabled: response.CardEnhancedVerificationEnabled,
    FileConversionEnabled: response.FileConversionEnabled,
    FileSubmissionEnabled: response.FileSubmissionEnabled,
    SupportTicketEnabled: response.SupportTicketEnabled,
    DigitalWalletsEnabled: response.DigitalWalletsEnabled,
    PaypalWithdrawEnabled: response.PaypalWithdrawEnabled,
    VenmoWithdrawEnabled: response.VenmoWithdrawEnabled,
    ZelleWithdrawEnabled: response.ZelleWithdrawEnabled,
  };

  function createLockedSelectOptions(): { [key: string]: string } {
    const lockedPaymentRails: { [key: string]: LockedType } = {
      creditCard: {
        id: 'Credit Card',
        name: 'Credit Card',
        locked: !vp.toBool(response.CreditCardEnabled),
      },
      debittCard: {
        id: 'Debit Card',
        name: 'Debit Card',
        locked: !response.AFTEnabled && !vp.toBool(response.PushToCardEnabled),
      },
      oct: {
        id: 'OCT',
        name: 'OCT',
        locked: !vp.toBool(response.PushToCardEnabled),
      },
      aft: {
        id: 'AFT',
        name: 'AFT',
        locked: !response.AFTEnabled,
      },

      email: {
        id: 'Email',
        name: 'Email',
        locked:
          !vp.toBool(response.RealTimeMoneyRequestEnabled) &&
          !vp.toBool(response.InteracMoneyRequestEnabled) &&
          !vp.toBool(response.InteracBulkPayoutEnabled),
      },
      interacEtransferMoneyRequest: {
        id: 'Interac e-Transfer Money Request',
        name: '<i>Interac</i> Money Request<sup>&reg;</sup>',
        locked: !vp.toBool(response.InteracMoneyRequestEnabled),
      },

      interacEtransfer: {
        id: 'Interac e-Transfer',
        name: 'Interac e-Transfer',
        locked: !vp.toBool(response.RealTimeMoneyRequestEnabled) && !vp.toBool(response.InteracBulkPayoutEnabled),
      },
      interac: {
        id: 'interac',
        name: 'Interac',
        locked: !vp.toBool(response.InteracMoneyRequestEnabled) && !vp.toBool(response.InteracBulkPayoutEnabled),
      },
      rtp: {
        id: 'RTP',
        name: 'RTP',
        locked: !vp.toBool(response.RTPWithdrawEnabled),
      },
      fedwireFund: {
        id: 'Fedwire Fund',
        name: 'Fedwire Fund',
        locked: !vp.toBool(response.FedwireWithdrawEnabled),
      },
      fedwireWithdraw: {
        id: 'Fedwire Withdraw',
        name: 'Fedwire Withdraw',
        locked: !vp.toBool(response.FedwireFundEnabled),
      },
      fedwire: {
        id: 'Fedwire',
        name: 'Fedwire',
        locked: !vp.toBool(response.FedwireWithdrawEnabled) && !vp.toBool(response.FedwireFundEnabled),
      },
      eft: {
        id: 'eft',
        name: 'EFT',
        locked: false,
      },
      EFTSendEnabled: {
        id: 'EFTSendEnabled',
        name: 'EFT Send',
        locked: !vp.toBool(response.EFTSendEnabled),
      },
      EFTCollectEnabled: {
        id: 'EFTCollectEnabled',
        name: 'EFT Collect',
        locked: !vp.toBool(response.EFTCollectEnabled),
      },
      RTRWithdrawEnabled: {
        id: 'RTRWithdrawEnabled',
        name: 'VoPay Instant',
        locked: !vp.toBool(response.RTRWithdrawEnabled),
      },
      ACHWithdrawEnabled: {
        id: 'ACHWithdrawEnabled',
        name: 'ACH Withdraw',
        locked: !vp.toBool(response.ACHWithdrawEnabled),
      },
      ACHFundEnabled: {
        id: 'ACHFundEnabled',
        name: 'ACH Fund',
        locked: !vp.toBool(response.ACHFundEnabled),
      },
      RTPWithdrawEnabled: {
        id: 'RTPWithdrawEnabled',
        name: 'RTP',
        locked: !vp.toBool(response.RTPWithdrawEnabled),
      },
      FedwireWithdrawEnabled: {
        id: 'FedwireWithdrawEnabled',
        name: 'Fedwire Withdraw',
        locked: !vp.toBool(response.FedwireWithdrawEnabled),
      },
      FedwireFundEnabled: {
        id: 'FedwireFundEnabled',
        name: 'Fedwire Fund',
        locked: false,
      },
      bankAccount: {
        id: 'Bank Account',
        name: 'Bank Account',
        locked:
          !vp.toBool(response.EFTCollectEnabled) &&
          !vp.toBool(response.EFTSendEnabled) &&
          !vp.toBool(response.ACHFundEnabled) &&
          !vp.toBool(response.ACHWithdrawEnabled) &&
          !vp.toBool(response.RTRWithdrawEnabled),
      },
      voPayInstant: {
        id: 'VoPay Instant',
        name: 'VoPay Instant',
        locked: !vp.toBool(response.RTRWithdrawEnabled),
      },
      usd: {
        id: 'USD',
        name: 'USD',
        locked:
          !vp.toBool(response.USDEFTCollectEnabled) &&
          !vp.toBool(response.USDEFTSendEnabled) &&
          !vp.toBool(response.ACHFundEnabled) &&
          !vp.toBool(response.ACHWithdrawEnabled),
      },
      cad: {
        id: 'CAD',
        name: 'CAD',
        locked: false,
      },
      paylink: {
        id: 'Paylink',
        name: 'I would like to request the Bank Account details from my Contact',
        locked: !vp.toBool(response.PayLinkEnabled),
      },
      googlePay: {
        id: 'Google Pay',
        name: 'Google Pay',
        locked: !response.GooglePayEnabled,
      },
      applePay: {
        id: 'Apple Pay',
        name: 'Apple Pay',
        locked: !response.ApplePayEnabled,
      },
      paypal: {
        id: 'PayPal',
        name: 'PayPal',
        locked: !response.PaypalWithdrawEnabled,
      },
      venmo: {
        id: 'Venmo',
        name: 'Venmo',
        locked: !response.VenmoWithdrawEnabled,
      },
      paypalWithdraw: {
        id: 'PayPal Withdraw',
        name: 'PayPal',
        locked: !response.PaypalWithdrawEnabled,
      },
      venmoWithdraw: {
        id: 'Venmo Withdraw',
        name: 'Venmo',
        locked: !response.VenmoWithdrawEnabled,
      },
      zelle: {
        id: 'Zelle',
        name: 'Zelle',
        locked: !response.ZelleWithdrawEnabled,
      },
      zelleWithdraw: {
        id: 'Zelle Withdraw',
        name: 'Zelle',
        locked: !response.ZelleWithdrawEnabled,
      },
      bankVerification: {
        id: 'Bank Account Verification',
        name: 'Bank Account Verification',
        locked: !response.MicroDepositBankAccountVerificationEnabled,
      },
      interacVerification: {
        id: 'Interac Verification',
        name: 'Interac Verification',
        locked: !response.InteracVerificationEnabled,
      },
      interacBasicVerification: {
        id: 'Interac Basic Verification',
        name: 'Interac Basic Verification',
        locked: !response.InteracBasicVerificationEnabled,
      },
      interacEnhancedVerification: {
        id: 'Interac Enhanced Verification',
        name: 'Interac Enhanced Verification',
        locked: !response.InteracEnhancedVerificationEnabled,
      },
    };

    return Object.values(lockedPaymentRails).reduce((filtered: { [key: string]: string }, option: LockedType) => {
      if (option.locked) {
        filtered[option.id] = option.name;
      }

      return filtered;
    }, {});
  }

  return <LockedFeaturesContext.Provider value={values}>{children}</LockedFeaturesContext.Provider>;
};

export const useLockedFeatures = (): LockedFeaturesCtx => useContext(LockedFeaturesContext);
